/**
 * P3-20: PolicyPanel —— 政策区（迁移自 classes.ui.PolicyPanel，
 * js/science.js:2602-2682）。
 *
 * 结构对齐：
 * - 50% 宽互斥说明文字 $I("msg.policy.exclusivity"）；
 * - 右上角两个 checkbox：policyToggleResearched（有已研究 policy 时显示）、
 *   policyToggleBlocked（有已阻塞 policy 时显示）；
 * - 全部政策按钮垂直列表；
 * - 面板整体可见性：任一 policy unlocked 时显示。
 */
import { useCallback } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { PolicyButton } from "./PolicyButton";

interface PolicyMeta {
  name: string;
  unlocked: boolean;
  researched: boolean;
  blocked: boolean;
}

interface PolicyPanelSnapshot {
  visible: boolean;
  showToggleResearched: boolean;
  showToggleBlocked: boolean;
  policyToggleResearched: boolean;
  policyToggleBlocked: boolean;
  policies: string[];
}

function computeSnapshot(game: GamePage | null): PolicyPanelSnapshot | null {
  if (!game) {
    return null;
  }
  const science = game.science as {
    policies: PolicyMeta[];
    policyToggleResearched: boolean;
    policyToggleBlocked: boolean;
  };
  const policies = science.policies;
  const visible = policies.some((p) => p.unlocked);
  const showToggleResearched = policies.some((p) => p.researched);
  const showToggleBlocked = policies.some((p) => p.blocked);
  return {
    visible,
    showToggleResearched,
    showToggleBlocked,
    policyToggleResearched: science.policyToggleResearched,
    policyToggleBlocked: science.policyToggleBlocked,
    policies: policies.map((p) => p.name),
  };
}

function snapshotEq(
  a: PolicyPanelSnapshot | null,
  b: PolicyPanelSnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.visible === b.visible &&
    a.showToggleResearched === b.showToggleResearched &&
    a.showToggleBlocked === b.showToggleBlocked &&
    a.policyToggleResearched === b.policyToggleResearched &&
    a.policyToggleBlocked === b.policyToggleBlocked &&
    a.policies.length === b.policies.length &&
    a.policies.every((name, i) => name === b.policies[i])
  );
}

export function PolicyPanel() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const toggleResearched = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    const science = game.science as { policyToggleResearched: boolean };
    science.policyToggleResearched = !science.policyToggleResearched;
  }, []);

  const toggleBlocked = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    const science = game.science as { policyToggleBlocked: boolean };
    science.policyToggleBlocked = !science.policyToggleBlocked;
  }, []);

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <div className="policyPanel">
      <span
        style={{
          display: "inline-block",
          marginBottom: "10px",
          width: "50%",
        }}
      >
        {t("msg.policy.exclusivity")}
      </span>
      <div style={{ float: "right" }}>
        <span
          style={{
            display: snap.showToggleResearched ? "" : "none",
          }}
        >
          <input
            id="policyToggleResearched"
            type="checkbox"
            checked={snap.policyToggleResearched}
            onChange={toggleResearched}
          />
          <label htmlFor="policyToggleResearched">
            {t("science.policyToggleResearched.label")}
          </label>
          <br />
        </span>
        <span
          style={{
            display: snap.showToggleBlocked ? "" : "none",
          }}
        >
          <input
            id="policyToggleBlocked"
            type="checkbox"
            checked={snap.policyToggleBlocked}
            onChange={toggleBlocked}
          />
          <label htmlFor="policyToggleBlocked">
            {t("science.policyToggleBlocked.label")}
          </label>
        </span>
      </div>
      {snap.policies.map((name) => (
        <PolicyButton key={name} policyName={name} />
      ))}
      <div style={{ clear: "both" }} />
    </div>
  );
}
